// Single source of truth for the `firearm` URL query param.
//
// Elastic search-ui owns its own URL state (q, current, size, filters,
// sort-field, sort-direction). We hand it custom `routingOptions` that
// strip `firearm` before Elastic sees the URL and re-merge it on every
// write. HomePage drives the slug via `setFirearmSlug` and reacts to
// back/forward via `subscribeFirearm`.

const FIREARM_PARAM = "firearm";

let currentFirearmSlug = null;
let lastElasticQS = "";
let nextWriteReplaces = false;
const subscribers = new Set();

// Mark the *next* Elastic-driven URL push as a replaceState. Used when a
// navigation already created the right URL entry (e.g. breadcrumb), so
// Elastic's follow-up push shouldn't add a second history entry.
export function replaceNextWrite() {
  nextWriteReplaces = true;
}

function notify() {
  subscribers.forEach((cb) => {
    try {
      cb(currentFirearmSlug);
    } catch (e) {
      console.error("firearm subscriber threw", e);
    }
  });
}

function buildUrl(qs) {
  const params = new URLSearchParams(qs || "");
  params.delete(FIREARM_PARAM);
  if (currentFirearmSlug) {
    params.set(FIREARM_PARAM, currentFirearmSlug);
  }
  const search = params.toString();
  return `${window.location.pathname}${search ? `?${search}` : ""}`;
}

export function getFirearmSlug() {
  return currentFirearmSlug;
}

export function subscribeFirearm(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// Update the slug and reflect it in the URL via replaceState (so a row
// click doesn't pollute browser back history). Pass { notify: true }
// to fan the change out to subscribers — used when an external caller
// (e.g., header logo click) wants HomePage to drop its selection.
export function setFirearmSlug(slug, { notify: shouldNotify = false } = {}) {
  const next = slug || null;
  if (next === currentFirearmSlug) return;
  currentFirearmSlug = next;
  // Read live URL params so a setFirearmSlug call doesn't clobber filters
  // that the page navigated to but Elastic hasn't pushed yet.
  const live = new URLSearchParams(window.location.search);
  live.delete(FIREARM_PARAM);
  lastElasticQS = live.toString();
  const url = buildUrl(lastElasticQS);
  window.history.replaceState(null, "", url);
  if (shouldNotify) notify();
}

export const routingOptions = {
  // Called by Elastic's URLManager on init. We seed our module refs
  // from the current URL so the first selection-sync effect in HomePage
  // doesn't clobber Elastic params it hasn't pushed yet.
  readUrl() {
    const params = new URLSearchParams(window.location.search);
    currentFirearmSlug = params.get(FIREARM_PARAM) || null;
    params.delete(FIREARM_PARAM);
    const qs = params.toString();
    lastElasticQS = qs;
    return qs ? `?${qs}` : "";
  },

  writeUrl(qs, { replaceUrl = false } = {}) {
    lastElasticQS = qs || "";
    // Don't pollute /firearm/<slug> with Elastic's main-engine URL state.
    if (window.location.pathname.startsWith("/firearm/")) return;
    const url = buildUrl(lastElasticQS);
    const shouldReplace = replaceUrl || nextWriteReplaces;
    nextWriteReplaces = false;
    if (shouldReplace) {
      window.history.replaceState(null, "", url);
    } else {
      window.history.pushState(null, "", url);
    }
  },

  routeChangeHandler(callback) {
    const handler = () => {
      const params = new URLSearchParams(window.location.search);
      const slug = params.get(FIREARM_PARAM) || null;
      if (slug !== currentFirearmSlug) {
        currentFirearmSlug = slug;
        notify();
      }
      params.delete(FIREARM_PARAM);
      const qs = params.toString();
      lastElasticQS = qs;
      callback(qs ? `?${qs}` : "");
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  },
};
